import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getIPFinalDischargeReportAPI } from "../../../services/orgDashboard.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import axiosInstance from "../../../config/axios.config";
import dayjs from "dayjs";
import useQuery from "../../../hooks/useQuery";
import {
  parsePageParam,
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../helpers/urlFilters";
import { flattenDischargeResults } from "./finalDischargeColumns";

const dateFormat = "YYYY-MM-DD";

export const useFinalDischargeReport = ({ enabled = true } = {}) => {
  const navigate = useNavigate();
  const params = useQuery();
  const facilityAdmin =
    getCookie(Role) === "Facility_Admin" ? getCookie(FacilityID) : null;
  const today = dayjs();
  const startOfMonth = dayjs().startOf("month");

  const searchText = parseSearchParam(params) || "";
  const page = parsePageParam(params);
  const [searchInput, setSearchInput] = useState(searchText);
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const date = useMemo(
    () => ({
      from_date: params?.from_date || startOfMonth.format(dateFormat),
      to_date: params?.to_date || today.format(dateFormat),
    }),
    [params?.from_date, params?.to_date]
  );
  const currentFacility = facilityAdmin
    ? Number(facilityAdmin)
    : params?.facility
      ? Number(params.facility)
      : null;

  const [facilityData, setFacilityData] = useState([]);
  const [singleFacilityName, setSingleFacilityName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columnConfig, setColumnConfig] = useState({});
  const [pagination, setPagination] = useState({
    current: page,
    pageSize: 100,
    total: 0,
  });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    getFacilityListApi()
      .then((res) => {
        const facilities = res?.data || [];
        const facilityOptions = facilities.map((option) => ({
          label: option.name,
          value: option.id,
        }));

        if (facilityOptions.length === 1) {
          setSingleFacilityName(facilityOptions[0].label);
          setFacilityData(facilityOptions);
        } else {
          setFacilityData([{ label: "All", value: null }, ...facilityOptions]);
        }
      })
      .catch((error) => console.log(`Error:${error}`));
  }, [enabled]);

  const onDateChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;

    updateUrlSearchParams(navigate, {
      from_date: dayjs(range[0]).format(dateFormat),
      to_date: dayjs(range[1]).format(dateFormat),
      page: 1,
    });
  };

  const fetchReport = async (pageNum = page, limit = 100) => {
    if (!enabled || !date?.from_date || !date?.to_date) return;

    setLoading(true);
    try {
      const res = await getIPFinalDischargeReportAPI(
        currentFacility,
        date,
        pageNum,
        limit,
        searchText
      );
      const payload = res?.data || {};
      const results = payload.results || [];

      setColumnConfig(payload.columns || {});
      setData(flattenDischargeResults(results));
      setPagination({
        current: Number(payload.current_page) || pageNum,
        pageSize: limit,
        total: payload.count ?? results.length,
      });
    } catch (error) {
      console.error(`Error:${error}`);
      setData([]);
      setColumnConfig({});
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (nextPagination) => {
    updateUrlSearchParams(navigate, { page: nextPagination.current });
    fetchReport(nextPagination.current, nextPagination.pageSize);
  };

  useEffect(() => {
    if (!enabled || !date?.from_date || !date?.to_date) return;
    fetchReport(page, pagination.pageSize);
    // eslint-disable-next-line
  }, [enabled, currentFacility, date.from_date, date.to_date, page, searchText]);

  const setSearchText = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateUrlSearchParams(navigate, { search: value || null, page: 1 });
    }, 1000);
  };

  const handleExport = async (type) => {
    if (!date?.from_date || !date?.to_date) {
      message.error("Please select the date filters to download the file");
      return;
    }

    try {
      const exportParams = {
        from_date: date.from_date,
        to_date: date.to_date,
        response_type: type,
        facility_id: currentFacility,
        search: searchText || undefined,
      };

      Object.keys(exportParams).forEach(
        (key) => exportParams[key] == null && delete exportParams[key]
      );

      const response = await axiosInstance.get(
        "/billing/dashboard/ip-final-discharge-report/",
        {
          params: exportParams,
          responseType: "blob",
        }
      );

      const blob = response.data;
      let filename = `ip_final_discharge_report.${type}`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      message.error("Failed to export report");
    }
  };

  const handleFacilityChange = (val) => {
    updateUrlSearchParams(navigate, { facility: val, page: 1 });
  };

  return {
    facilityAdmin,
    today,
    date,
    facilityData,
    currentFacility,
    handleFacilityChange,
    singleFacilityName,
    searchText: searchInput,
    setSearchText,
    loading,
    data,
    columnConfig,
    pagination,
    onDateChange,
    handleTableChange,
    handleExport,
  };
};
